import { DownloadOutlined, EyeOutlined, SearchOutlined, UploadOutlined } from "@ant-design/icons";
import type { TableProps } from "antd";
import { Button, Col, ConfigProvider, DatePicker, Drawer, Flex, Input, Layout, Modal, Row, Skeleton, Table, Upload, notification } from "antd";
import { SorterResult } from "antd/es/table/interface";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { LoaderSpinner } from "../../../components/common/Loader";
import {
    downloadVendorInvoicePaymentFile,
    getVendorInvoicePaymentFileDetails,
    getVendorInvoicePaymentFiles,
    saveVendorInvoicePaymentFile,
    uploadReviewVendorInvoicePaymentFile
} from "../../../service/httpsCalls";
import { VendorPaymentFileConstants, VendorPaymentFileMessages } from "./VendorPaymentFileConstants";
import { createTableConfig, VendorPaymentFileColumnsConfig, VendorPaymentReviewColumnsConfig } from "../tableConfig";
import "../billing.scss";

const { RangePicker } = DatePicker;

type NotificationType = "success" | "info" | "warning" | "error";
type DrawerMode = "upload" | "view";

interface PaymentFileInfo {
    sid: number;
    fileName: string;
    filePath: string;
    fileExtension: string;
    status: string;
    recordCount: number;
    processedRecordCount: number;
    totalAmount: number;
    processedTotalAmount: number;
    createdOn: string;
    createdBy: string;
    updatedOn: string;
    updatedBy: string;
    canSave: boolean;
    canDelete: boolean;
}

interface PaymentFileRecord {
    rowNumber: number;
    processDate: string;
    vendor: string;
    vendorTaxId: string;
    invoiceNumber: string;
    paymentReference: string;
    paymentMethod: string;
    disbursementReference: string;
    amount: number;
    checkDate: string;
    eopNumber: string;
    eopAmount: number | null;
    mappingStatus: string;
    mappingReason: string;
    isEligibleForSave: boolean;
}

interface PaymentFileDetails {
    info: PaymentFileInfo;
    records: PaymentFileRecord[];
}

function downloadBlob(response: any, defaultFileName: string) {
    const blob = new Blob([response.data], { type: response.headers["content-type"] });
    const url = window.URL.createObjectURL(blob);

    const contentDisposition = response.headers["content-disposition"] ?? "";
    const matchedName = /filename\*?=(?:UTF-8''|\")?([^;\"]+)/i.exec(contentDisposition);
    const fileName = matchedName?.[1] ? decodeURIComponent(matchedName[1].replace(/\"/g, "")) : defaultFileName;

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export default function ImportPaymentFile() {
    const [api, contextHolder] = notification.useNotification();
    const [tableHeight, setTableHeight] = React.useState(300);
    const [recordsContainerHeight, setRecordsContainerHeight] = React.useState(360);
    const screenWidth = window.innerWidth;
    const drawerScreenWidth = screenWidth * 0.7;
    const percentageWidth = screenWidth * 0.9;
    const defaultDrawerWidth = 1000;

    const [fileName, setFileName] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);

    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortedInfo, setSortedInfo] = useState<SorterResult<any>>({});

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState<DrawerMode>("upload");
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [detail, setDetail] = useState<PaymentFileDetails | null>(null);
    const [downloadingFileSid, setDownloadingFileSid] = useState<number | null>(null);

    const [uploadState, setUploadState] = useState<any>({
        file: null,
        fileList: [],
        loading: false,
        saving: false
    });
    const uploadRequestInFlight = React.useRef(false);

    const openNotification = (type: NotificationType, message: string, description = "") => {
        api[type]({ message, description });
    };

    const getErrorMessage = (res: any): string => {
        return res?.error?.messages?.length > 0
            ? res.error.messages[0].description
            : VendorPaymentFileConstants.defaultErrorMessage;
    };

    function loadPaymentFiles(isReset?: boolean) {
        setLoading(true);
        getVendorInvoicePaymentFiles({
            pageIndex: currentPage,
            pageSize,
            sortField: sortedInfo.order ? sortedInfo.field : null,
            sortDescending: (sortedInfo.order ??  "descend") === "descend",
            fileName: !isReset ? fileName ?? null : null,
            status: VendorPaymentFileConstants.statusProcessed,
            fromUploadedDate: !isReset && startDate ? dayjs(startDate).format(VendorPaymentFileConstants.dateFormatApi) : null,
            toUploadedDate: !isReset && endDate ? dayjs(endDate).format(VendorPaymentFileConstants.dateFormatApi) : null
        }, (res: any) => {
            if (res !== "error") {
                setRows(res?.items ?? []);
                setTotalRecords(Number(res?.totalRecords ?? 0));
                if (!showTable) {
                    setShowTable(true);
                }
            } else {
                setRows([]);
                setTotalRecords(0);
            }
            setLoading(false);
        });
    }

    function search() {
        if (currentPage === 1) {
            loadPaymentFiles();
        } else {
            setCurrentPage(1);
        }
    }

    function resetFilters() {
        setFileName(null);
        setStartDate(null);
        setEndDate(null);
        if (currentPage === 1) {
            loadPaymentFiles(true);
        } else {
            setCurrentPage(1);
        }
    }

    function resetDrawerState() {
        uploadRequestInFlight.current = false;
        setDetail(null);
        setDownloadingFileSid(null);
        setUploadState({
            file: null,
            fileList: [],
            loading: false,
            saving: false
        });
    }

    function openUploadDrawer() {
        resetDrawerState();
        setDrawerMode("upload");
        setDrawerOpen(true);
    }

    function openViewDrawer(record: any) {
        resetDrawerState();
        setDrawerMode("view");
        setDrawerOpen(true);
        fetchPaymentFileDetail(record.sid);
    }

    function fetchPaymentFileDetail(paymentFileSid: number) {
        setDrawerLoading(true);
        getVendorInvoicePaymentFileDetails(paymentFileSid, (res: any) => {
            setDrawerLoading(false);
            if (res?.hasError) {
                openNotification("warning", getErrorMessage(res));
                return;
            }

            setDetail(res);
        });
    }

    function clearSelectedUploadFile() {
        setUploadState((prev: any) => ({
            ...prev,
            file: null,
            fileList: []
        }));
    }

    function onUploadChange({ file, fileList }) {
        if (file?.status === "removed") {
            clearSelectedUploadFile();
            return;
        }

        const selectedFileName = (file?.name ?? "").toLowerCase();
        if (!selectedFileName.endsWith(VendorPaymentFileConstants.fileExtensionXlsx)) {
            openNotification("warning", VendorPaymentFileMessages.onlyXlsxFileAllowed);
            clearSelectedUploadFile();
            return Upload.LIST_IGNORE;
        }

        setUploadState((prev: any) => ({
            ...prev,
            file: file.originFileObj || file,
            fileList
        }));
    }

    function uploadAndReview() {
        if (uploadState.loading || uploadState.saving || uploadRequestInFlight.current) {
            return;
        }
        const selectedFile = uploadState.file;
        if (!selectedFile) {
            const hasUploadSelection = (uploadState?.fileList?.length ?? 0) > 0;
            openNotification(
                "warning",
                hasUploadSelection
                    ? VendorPaymentFileMessages.uploadedFileNotFoundToImport
                    : VendorPaymentFileMessages.selectXlsxFile
            );
            return;
        }

        uploadRequestInFlight.current = true;
        setUploadState((prev: any) => ({ ...prev, loading: true }));
        uploadReviewVendorInvoicePaymentFile({ file: selectedFile }, (res: any) => {
            uploadRequestInFlight.current = false;
            setUploadState((prev: any) => ({ ...prev, loading: false }));
            if (res?.hasError) {
                openNotification("warning", getErrorMessage(res));
                return;
            }

            setDetail(res);
            openNotification("success", VendorPaymentFileMessages.importReviewSuccess);
        });
    }

    function savePaymentFileData() {
        if (uploadState.loading || uploadState.saving) {
            return;
        }

        if (!detail?.records?.length) {
            openNotification("warning", VendorPaymentFileMessages.noPreviewRowsToSave);
            return;
        }

        if (!uploadState.file) {
            openNotification("warning", VendorPaymentFileMessages.uploadedFileRequiredToSave);
            return;
        }

        Modal.confirm({
            title: VendorPaymentFileMessages.saveConfirmTitle,
            okText: "Yes",
            cancelText: "No",
            onOk() {
                setUploadState((prev: any) => ({ ...prev, saving: true }));
                saveVendorInvoicePaymentFile({
                    file: uploadState.file,
                    rows: detail.records
                }, (res: any) => {
                    setUploadState((prev: any) => ({ ...prev, saving: false }));
                    if (res?.hasError) {
                        openNotification("warning", getErrorMessage(res));
                        return;
                    }

                    setDetail(res);
                    setDrawerMode("view");
                    openNotification("success", VendorPaymentFileMessages.saveSuccess);
                    if (currentPage === 1) {
                        loadPaymentFiles();
                    } else {
                        setCurrentPage(1);
                    }
                });
            }
        });
    }

    function cancelPreview() {
        setDetail(null);
        clearSelectedUploadFile();
    }

    function downloadPaymentFileData(fileSid?: number, fileNameForDownload?: string) {
        const sid = fileSid ?? detail?.info?.sid;
        if (!sid) {
            return;
        }

        setDownloadingFileSid(sid);
        downloadVendorInvoicePaymentFile(sid, (res: any) => {
            setDownloadingFileSid(null);
            if (res?.hasError) {
                openNotification("warning", getErrorMessage(res));
                return;
            }

            downloadBlob(res, fileNameForDownload || detail?.info?.fileName || VendorPaymentFileConstants.downloadFallbackFileName);
            openNotification("success", VendorPaymentFileMessages.downloadSuccess);
        });
    }

    const handleTableChange: TableProps<any>["onChange"] = (pagination, filters, sorter) => {
        setCurrentPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);
        setSortedInfo(sorter as SorterResult<any>);
    };

    useEffect(() => {
        document.title = VendorPaymentFileConstants.pageTitle;
    }, []);

    useEffect(() => {
        loadPaymentFiles();
    }, [currentPage, pageSize, sortedInfo]);

    useEffect(() => {
        if (!drawerOpen || !detail?.records?.length) {
            return;
        }

        const updateHeight = () => {
            const wrapper = document.getElementById("importpaymentfile-table-wrapper");
            const container = document.getElementById("importpaymentfile-table-container");
            if (!wrapper || !container) {
                return;
            }

            const actions = document.getElementById("importpaymentfile-record-actions");
            const actionsHeight = actions?.offsetHeight ?? 0;
            const wrapperTop = wrapper.getBoundingClientRect().top;
            const bottomPadding = 32;
            const availableHeight = Math.floor(window.innerHeight - wrapperTop - bottomPadding - actionsHeight);
            const nextContainerHeight = Math.max(220, availableHeight);
            setRecordsContainerHeight(nextContainerHeight);

            const header = container.getElementsByClassName("ant-table-header")[0] as HTMLElement | undefined;
            const headerHeight = header?.clientHeight ?? 10;
            setTableHeight(Math.max(160, nextContainerHeight - headerHeight));
        };

        const timerId = window.setTimeout(updateHeight, 0);
        const timerId2 = window.setTimeout(updateHeight, 100);
        window.addEventListener("resize", updateHeight);

        return () => {
            window.clearTimeout(timerId);
            window.clearTimeout(timerId2);
            window.removeEventListener("resize", updateHeight);
        };
    }, [drawerOpen, detail?.records?.length, detail?.info?.status, isPreviewState]);

    const paymentFileColumns = useMemo(() => createTableConfig(VendorPaymentFileColumnsConfig), []);
    const reviewColumns = useMemo(() => createTableConfig(VendorPaymentReviewColumnsConfig), []);

    const tableColumnsWithAction = [
        ...paymentFileColumns,
        {
            title: "Actions",
            dataIndex: "actions",
            width: 90,
            render: (text: any, record: any) => (
                <Button
                    onClick={() => openViewDrawer(record)}
                    style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9", color: "rgba(0, 0, 0, 0.88)" }}
                    type="primary"
                    icon={<EyeOutlined />}
                />
            )
        }
     ];

    const currentStatus = (detail?.info?.status ?? "").toLowerCase();
    const isPreviewState = !!detail && currentStatus === VendorPaymentFileConstants.statusImportedLower && (detail?.info?.sid ?? 0) <= 0;
    const isProcessedStatus = currentStatus === VendorPaymentFileConstants.statusProcessedLower;
    const canDownloadImportedFile = isProcessedStatus && (detail?.info?.sid ?? 0) > 0;
    const showUploadSection = drawerMode === "upload" && !detail;
    const hasGridData = (detail?.records?.length ?? 0) > 0;
    const drawerWidth = hasGridData
        ? Math.max(percentageWidth, 1250)
        : Math.max(drawerScreenWidth, defaultDrawerWidth);

    const formatCurrency = (value: number | null | undefined) => new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(value ?? 0));

    const selectedFileName = uploadState?.fileList?.[0]?.name ?? "";

    return <ConfigProvider theme={{
        components: {
            Layout: { bodyBg: "#eff1f5", siderBg: "#fff" }
        }
    }}>
        {contextHolder}
        <Layout style={{ padding: "20px", marginBottom: "5px", marginTop: "5px" }}>
            <Drawer
                title="Import Payment File"
                open={drawerOpen}
                width={drawerWidth}
                onClose={() => {
                    setDrawerOpen(false);
                    resetDrawerState();
                }}
                destroyOnClose
                maskClosable={false}
                styles={{ body: { background: "#eff1f5" } }}
            >
                <Skeleton loading={drawerLoading} active>
                    {showUploadSection &&
                        <div style={{ background: "#fff", padding: "15px" }}>
                            <Row gutter={10}>
                                <Col span={24}>
                                    <Flex align="center" gap="small">
                                        <Upload
                                            maxCount={1}
                                            showUploadList={false}
                                            fileList={uploadState.fileList}
                                            onChange={onUploadChange}
                                            beforeUpload={() => false}
                                            accept={VendorPaymentFileConstants.fileExtensionXlsx}
                                            disabled={uploadState.loading || uploadState.saving}
                                        >
                                            <Button disabled={uploadState.loading || uploadState.saving}>
                                                <UploadOutlined /> Click to Upload
                                            </Button>
                                        </Upload>
                                        <Input
                                            allowClear
                                            disabled={false}
                                            value={selectedFileName}
                                            placeholder="No file selected"
                                            onKeyDown={(e) => e.preventDefault()}
                                            onPaste={(e) => e.preventDefault()}
                                            onDrop={(e) => e.preventDefault()}
                                            onChange={(event) => {
                                                if (event.target.value === "") {
                                                    clearSelectedUploadFile();
                                                }
                                            }}
                                            style={{ flex: 1 }}
                                        />
                                        <Button type="primary" loading={uploadState.loading} onClick={uploadAndReview} disabled={uploadState.loading || uploadState.saving}>
                                            Import & Review
                                        </Button>
                                    </Flex>
                                </Col>
                            </Row>
                        </div>
                    }

                    {detail?.info &&
                        <div style={{ marginTop: "12px", background: "#fff", padding: "15px" }}>
                            <Flex justify="space-between" align="center" style={{ marginBottom: "8px" }}>
                                <p
                                    style={{
                                        fontSize: 14,
                                        color: "rgba(0, 0, 0, 0.85)",
                                        marginTop: "0px",
                                        marginBottom: "0px",
                                        fontWeight: 500
                                    }}
                                >
                                    File Information
                                </p>
                                {canDownloadImportedFile &&
                                    <Button
                                        onClick={() => downloadPaymentFileData(detail.info.sid, detail.info.fileName)}
                                        loading={downloadingFileSid === detail.info.sid}
                                        disabled={downloadingFileSid !== null}
                                        type="primary"
                                        icon={<DownloadOutlined />}
                                    >
                                        Download Imported File
                                    </Button>
                                }
                            </Flex>
                            <div style={{ overflowWrap: "anywhere", paddingBottom: "6px" }}>
                                <strong>Name:</strong> {detail.info.fileName}&nbsp;&nbsp;|&nbsp;&nbsp;
                                <strong>Status:</strong> {detail.info.status}&nbsp;&nbsp;|&nbsp;&nbsp;
                                <strong>Imported By:</strong> {detail.info.createdBy}&nbsp;&nbsp;|&nbsp;&nbsp;
                                <strong>Imported Date:</strong> {detail.info.createdOn ? dayjs(detail.info.createdOn).format(VendorPaymentFileConstants.dateTimeFormatDisplay) : ""}&nbsp;&nbsp;|&nbsp;&nbsp;
                                <strong>Imported Count:</strong> {detail.info.recordCount}&nbsp;&nbsp;|&nbsp;&nbsp;
                                <strong>Imported Total Amount:</strong> {formatCurrency(detail.info.totalAmount)}
                                {isProcessedStatus &&
                                    <>
                                        &nbsp;&nbsp;|&nbsp;&nbsp;
                                        <strong>Processed Count:</strong> {detail.info.processedRecordCount}&nbsp;&nbsp;|&nbsp;&nbsp;
                                        <strong>Processed Total Amount:</strong> {formatCurrency(detail.info.processedTotalAmount)}
                                    </>
                                }
                            </div>
                        </div>
                    }

                    {detail?.info &&
                        <Flex style={{ marginTop: "12px", padding: "1rem", background: "#fff" }}>
                            <div style={{ width: "100%" }}>
                                <div id="importpaymentfile-table-wrapper" style={{ width: "100%" }}>
                                    <div id="importpaymentfile-table-container" style={{ height: `${recordsContainerHeight}px`, width: "100%", overflowY: "hidden", overflowX: "auto" }}>
                                        <Table
                                            dataSource={detail.records}
                                            columns={reviewColumns}
                                            rowKey={(record: PaymentFileRecord) => `${record.rowNumber}`}
                                            size="small"
                                            loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: uploadState.loading || uploadState.saving }}
                                            pagination={false}
                                            rowClassName={(record: PaymentFileRecord) => !record.isEligibleForSave ? "buspass-invalid-row" : ""}
                                            scroll={{ x: 2400, y: tableHeight }}
                                        />
                                    </div>
                                </div>

                                {isPreviewState &&
                                    <div id="importpaymentfile-record-actions" style={{ marginTop: "8px" }}>
                                        <Flex justify="end" gap="small">
                                            <Button type="primary" loading={uploadState.saving} onClick={savePaymentFileData} disabled={uploadState.loading}>Save</Button>
                                            <Button onClick={cancelPreview} disabled={uploadState.loading || uploadState.saving}>Cancel</Button>
                                        </Flex>
                                    </div>
                                }
                            </div>
                        </Flex>
                    }
                </Skeleton>
            </Drawer>

            <h3 style={{ textTransform: "uppercase", marginTop: "0px" }}>{VendorPaymentFileConstants.pageTitle}</h3>
            <Flex justify="space-between" style={{ padding: "1rem", alignItems: "center", background: "#fff" }}>
                <label><SearchOutlined />&nbsp;Search</label>
                <Button type="primary" icon={<UploadOutlined />} onClick={openUploadDrawer}>Import File</Button>
            </Flex>

            <Skeleton loading={false} active>
                <Row style={{ padding: "1rem", paddingTop: "0px", background: "#fff" }}>
                    <Col span={12}>
                        <Input
                            value={fileName ?? ""}
                            placeholder="File Name"
                            style={{ width: "95%" }}
                            allowClear
                            onChange={(e) => setFileName(e.target.value || null)}
                        />
                    </Col>
                    <Col span={12}>
                        <RangePicker
                            placeholder={["From Imported Date", "To Imported Date"]}
                            style={{ width: "100%" }}
                            format={VendorPaymentFileConstants.dateFormatDisplay}
                            value={[startDate, endDate]}
                            onChange={(dates: any) => {
                                if (dates) {
                                    setStartDate(dates[0]);
                                    setEndDate(dates[1]);
                                } else {
                                    setStartDate(null);
                                    setEndDate(null);
                                }
                            }}
                        />
                    </Col>
                </Row>

                <Flex justify="end" style={{ marginBottom: "2rem", padding: "1rem", paddingTop: "0px", background: "#fff" }} gap="small">
                    <Button type="primary" loading={loading} onClick={search}>Search</Button>
                    <Button onClick={resetFilters} style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9", color: "rgba(0, 0, 0, 0.88)" }}>
                        Reset
                    </Button>
                </Flex>
            </Skeleton>

            {showTable &&
                <Table style={{ background: "#fff" }}
                    dataSource={rows}
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: totalRecords,
                        showTotal: (total, range) => `${range[0] || 0}-${range[1] || 1} of ${total} items`,
                        showSizeChanger: true
                    }}
                    columns={tableColumnsWithAction}
                    onChange={handleTableChange}
                    size="small"
                    rowKey="sid"
                    loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: loading }}
                    scroll={{ y: rows?.length > 0 ? "calc(100vh - 520px)" : undefined }}
                />
            }
        </Layout>
    </ConfigProvider>;
}
